import { Link } from '@tanstack/react-router';
import { Play } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { addDays, toDateTimeLocalValue } from '@/lib/route-search';

import {
  useOnlineEvalAdHocRuns,
  useOnlineEvalEvaluators,
  usePreviewOnlineEvalAdHocRun,
  useStartOnlineEvalAdHocRun,
} from './online-eval.query';

import type {
  OnlineEvalAdHocRun,
  OnlineEvalAdHocRunRequest,
  OnlineEvalAdHocRunStatus,
} from './online-eval.types';

// Older chats have lost the system prompt and tools the evaluator needs; the backend refuses them
const MAX_MESSAGE_AGE_DAYS = 20;

type RunForm = {
  evaluator: string;
  emailPattern: string;
  messagesCreatedAfter: string;
  messagesCreatedBefore: string;
};

const initialForm = (): RunForm => {
  const now = new Date();
  return {
    evaluator: '',
    emailPattern: '%@littlebird.ai',
    messagesCreatedAfter: toDateTimeLocalValue(addDays(now, -7)),
    messagesCreatedBefore: toDateTimeLocalValue(now),
  };
};

const toRequest = (form: RunForm): OnlineEvalAdHocRunRequest => ({
  evaluator: form.evaluator,
  email_pattern: form.emailPattern,
  messages_created_after: new Date(form.messagesCreatedAfter).toISOString(),
  messages_created_before: new Date(form.messagesCreatedBefore).toISOString(),
});

const STATUS_VARIANT: Record<
  OnlineEvalAdHocRunStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  running: 'default',
  completed: 'secondary',
  failed: 'destructive',
  cancelled: 'outline',
  terminated: 'outline',
  timed_out: 'destructive',
  unknown: 'outline',
};

const formatDate = (value: string) => new Date(value).toLocaleString();

export const OnlineEvalAdHocRunsPage = () => {
  const evaluatorsQuery = useOnlineEvalEvaluators();
  const runsQuery = useOnlineEvalAdHocRuns();
  const previewRun = usePreviewOnlineEvalAdHocRun();
  const startRun = useStartOnlineEvalAdHocRun();
  const [form, setForm] = useState<RunForm>(initialForm);
  const evaluator = form.evaluator || evaluatorsQuery.data?.[0] || '';
  const request = toRequest({ ...form, evaluator });
  // A preview only describes the inputs it was made for, so any edit clears it
  const update = (change: Partial<RunForm>) => {
    previewRun.reset();
    setForm({ ...form, ...change });
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-medium text-primary">Online eval</p>
        <h1 className="text-3xl font-semibold tracking-tight">Ad hoc runs</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Evaluate recent chats of the users matching an email pattern with an
          evaluator and the current issue categories, to compare evaluators or
          try category changes. Replies that evaluation already judged are
          skipped, and the results are kept like a scheduled run&apos;s. Up to
          five runs at a time.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Start a run</CardTitle>
          <CardDescription>
            The email pattern uses % as the wildcard. The period must start
            within the last {MAX_MESSAGE_AGE_DAYS} days.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Select
              aria-label="Evaluator"
              value={evaluator}
              onChange={(event) => update({ evaluator: event.target.value })}
            >
              {(evaluatorsQuery.data ?? []).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            <Input
              aria-label="Email pattern"
              placeholder="%@littlebird.ai"
              value={form.emailPattern}
              onChange={(event) => update({ emailPattern: event.target.value })}
            />
            <Input
              aria-label="Replies after"
              type="datetime-local"
              value={form.messagesCreatedAfter}
              onChange={(event) =>
                update({ messagesCreatedAfter: event.target.value })
              }
            />
            <Input
              aria-label="Replies before"
              type="datetime-local"
              value={form.messagesCreatedBefore}
              onChange={(event) =>
                update({ messagesCreatedBefore: event.target.value })
              }
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              disabled={!evaluator || previewRun.isPending}
              onClick={() => previewRun.mutate(request)}
            >
              Preview
            </Button>
            <Button
              disabled={!previewRun.data || startRun.isPending}
              onClick={() =>
                startRun.mutate(request, {
                  onSuccess: () => previewRun.reset(),
                })
              }
            >
              <Play className="mr-2 h-4 w-4" /> Start
            </Button>
            {previewRun.data && (
              <span className="text-sm text-muted-foreground">
                {previewRun.data.chat_count.toLocaleString()} chats, up to{' '}
                {previewRun.data.message_count.toLocaleString()} replies to
                judge with categories version{' '}
                {previewRun.data.issue_categories_version_id}
              </span>
            )}
            {(previewRun.error ?? startRun.error) && (
              <span className="text-sm text-destructive">
                {(previewRun.error ?? startRun.error)?.message}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {runsQuery.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          Loading ad hoc runs…
        </div>
      ) : runsQuery.error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {runsQuery.error.message}
        </div>
      ) : (
        <AdHocRunsTable runs={runsQuery.data ?? []} />
      )}
    </div>
  );
};

const AdHocRunsTable = ({ runs }: { runs: OnlineEvalAdHocRun[] }) =>
  runs.length === 0 ? (
    <div className="rounded-lg border py-16 text-center text-sm text-muted-foreground">
      No ad hoc runs yet.
    </div>
  ) : (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Started</TableHead>
            <TableHead>Evaluation</TableHead>
            <TableHead>Users and period</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => (
            <TableRow key={run.id}>
              <TableCell className="whitespace-nowrap text-xs">
                <div>{formatDate(run.started_at)}</div>
                <div className="text-muted-foreground">{run.started_by}</div>
              </TableCell>
              <TableCell>
                <div>{run.evaluator}</div>
                <div className="text-xs text-muted-foreground">
                  Categories version {run.issue_categories_version_id}
                </div>
              </TableCell>
              <TableCell className="text-xs">
                <div>{run.email_pattern}</div>
                <div className="text-muted-foreground">
                  {formatDate(run.messages_created_after)} to{' '}
                  {formatDate(run.messages_created_before)}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={STATUS_VARIANT[run.status]}>
                  {run.status.replace('_', ' ')}
                </Badge>
              </TableCell>
              <TableCell className="text-xs">
                {run.chats_evaluated.toLocaleString()} of{' '}
                {run.chats_selected.toLocaleString()} chats,{' '}
                {run.messages_evaluated.toLocaleString()} replies,{' '}
                {run.findings.toLocaleString()} findings
              </TableCell>
              <TableCell className="text-right">
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to="/online-eval/issues"
                    search={(previous) => ({
                      ...previous,
                      evaluator: run.evaluator,
                      issueCategoriesVersionId: String(
                        run.issue_categories_version_id
                      ),
                    })}
                  >
                    View findings
                  </Link>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
